import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase, TABLES, SafeIcon } from '../../lib/core';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiClock, FiCheckCircle, FiX, FiZap, FiAlertTriangle, FiCheck, FiUserPlus, FiShield } = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0, hasFixed: false });
  const [invitations, setInvitations] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (user) {
      loadStats();
      fetchInvites();
      fetchPendingReviews();
    }
  }, [user]);

  const loadStats = async () => {
    // 1. Get all master relationships to check for fixed salary flags
    const { data: relations } = await supabase
      .from(TABLES.PILOT_MASTERS)
      .select('master_id, is_base_pay_only')
      .eq('pilot_id', user.id);

    const fixedMasters = new Set(relations?.filter(r => r.is_base_pay_only).map(r => r.master_id) || []);
    
    // 2. Fetch logs
    const { data: logs } = await supabase
      .from(TABLES.LOGS)
      .select('value_at_log, status, master_id')
      .eq('pilot_id', user.id);

    // 3. Aggregate only if master is NOT fixed-salary
    const summary = (logs || []).reduce((acc, log) => {
      if (!fixedMasters.has(log.master_id)) {
        acc.total += Number(log.value_at_log) || 0;
        if (log.status === 'unpaid') acc.pending += Number(log.value_at_log) || 0;
        if (log.status === 'paid') acc.paid += Number(log.value_at_log) || 0;
      } else {
        acc.hasFixed = true;
      }
      return acc;
    }, { total: 0, pending: 0, paid: 0, hasFixed: fixedMasters.size > 0 });

    setStats(summary);
  };

  const fetchInvites = async () => {
    const { data } = await supabase
      .from(TABLES.PILOT_MASTERS)
      .select('*, master:master_id(id, username, nickname)')
      .eq('pilot_id', user.id)
      .eq('status', 'pending');
    setInvitations(data || []);
  };

  const fetchPendingReviews = async () => {
    const { data } = await supabase
      .from(TABLES.LOGS)
      .select('*, item:item_id(name)')
      .eq('pilot_id', user.id)
      .eq('status', 'pending_pilot');
    setPendingReviews(data || []);
    setLoading(false);
  };

  const handleReviewAction = async (logId, action) => {
    let status = 'unpaid';
    let response = '';
    if (action === 'contest') {
      response = prompt("Why are you contesting this price?");
      if (!response) return;
      status = 'contested';
    }
    setProcessingId(logId);
    try {
      const { error } = await supabase.from(TABLES.LOGS).update({ status, pilot_response: response }).eq('id', logId);
      if (error) throw error;
      fetchPendingReviews();
      loadStats();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleInviteAction = async (pmId, action) => {
    setProcessingId(pmId);
    try {
      if (action === 'approve') {
        await supabase.from(TABLES.PILOT_MASTERS).update({ status: 'active' }).eq('id', pmId);
      } else {
        await supabase.from(TABLES.PILOT_MASTERS).delete().eq('id', pmId);
      }
      fetchInvites();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="space-y-12 pb-20">
      <header>
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">Pilot Dashboard</h2>
        <p className="text-gray-500 font-bold mt-2 italic">Earnings overview and active master contracts.</p>
        {stats.hasFixed && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl border border-amber-100">
            <FiShield className="text-amber-600" />
            <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Fixed Salary Mode Active</span>
          </div>
        )}
      </header>

      {(invitations.length > 0 || pendingReviews.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {invitations.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-[10px] font-black text-blue-600 uppercase tracking-widest ml-4 flex items-center gap-2">
                <FiUserPlus /> Team Invites
              </h3>
              {invitations.map(invite => (
                <div key={invite.id} className="bento-card border-l-4 border-l-blue-500 bg-blue-50/20 flex items-center justify-between">
                  <div>
                    <h4 className="font-black text-gray-900">{invite.master?.nickname || invite.master?.username}</h4>
                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Wants to recruit you</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleInviteAction(invite.id, 'approve')} className="p-3 bg-emerald-600 text-white rounded-xl shadow-lg"><FiCheck /></button>
                    <button onClick={() => handleInviteAction(invite.id, 'reject')} className="p-3 bg-red-500 text-white rounded-xl shadow-lg"><FiX /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {pendingReviews.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-[10px] font-black text-amber-600 uppercase tracking-widest ml-4 flex items-center gap-2">
                <FiAlertTriangle /> Price Adjustments
              </h3>
              {pendingReviews.map(log => (
                <div key={log.id} className="bento-card border-l-4 border-l-amber-500 bg-amber-50/20">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-black text-gray-900">{log.item?.name}</h4>
                      <p className="text-[10px] font-bold text-amber-600 italic">Adjusted to: ₱{log.value_at_log.toLocaleString()}</p>
                      <p className="text-[9px] text-gray-400 mt-1">Reason: {log.master_adjustment_reason}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleReviewAction(log.id, 'accept')} className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest">Accept</button>
                    <button onClick={() => handleReviewAction(log.id, 'contest')} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest">Contest</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-blue-500">
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-4"><FiZap /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Lifetime Loot</h3>
          <p className="text-3xl font-black text-gray-900">
            {stats.total > 0 ? `₱${stats.total.toLocaleString()}` : (stats.hasFixed ? 'Contracted' : '₱0')}
          </p>
        </div>
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-amber-500">
          <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-4"><FiClock /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Unpaid Earnings</h3>
          <p className="text-3xl font-black text-amber-600">
            {stats.pending > 0 ? `₱${stats.pending.toLocaleString()}` : (stats.hasFixed ? 'Active' : '₱0')}
          </p>
        </div>
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm border-l-4 border-l-emerald-500">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-4"><FiCheckCircle /></div>
          <h3 className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-1">Settled Payouts</h3>
          <p className="text-3xl font-black text-emerald-600">
            {stats.paid > 0 ? `₱${stats.paid.toLocaleString()}` : (stats.hasFixed ? 'Settled' : '₱0')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;